import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Search,
  ArrowUpDown,
  Archive,
  ArchiveRestore,
  Pencil,
  Trash2,
  Users,
} from "lucide-react";
import PageHeader from "./PageHeader";
import { formatCost, formatEur } from "./cost";
import { navigate, useSegmente } from "./router";

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function PartnerDetail({ partner, partnerId, onBack, onOpenStellenanzeigen }) {
  const [stellenanzeigen, setStellenanzeigen] = useState([]);
  const [jfCount, setJfCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`/api/partners/${partnerId}/stellenanzeigen`, { credentials: "include" }).then((r) => r.json()).catch(() => []),
      fetch(`/api/partners/jotform-counts`, { credentials: "include" }).then((r) => r.json()).catch(() => ({})),
    ]).then(([anzeigen, counts]) => {
      if (cancelled) return;
      if (Array.isArray(anzeigen)) setStellenanzeigen(anzeigen);
      // counts liefert je Unternehmen { count, generated_count } — best match per Name
      if (counts && partner?.unternehmen && counts[partner.unternehmen]) {
        setJfCount(parseInt(counts[partner.unternehmen].count || 0, 10));
      } else {
        setJfCount(0);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [partnerId, partner?.unternehmen]);

  const generated = stellenanzeigen.length;
  const totalTokens = stellenanzeigen.reduce((sum, a) => sum + (parseInt(a.total_tokens, 10) || 0), 0);
  const totalCostEur = stellenanzeigen.reduce((sum, a) => sum + (Number(a.cost_eur) || 0), 0);
  const cost = totalCostEur > 0 ? formatEur(totalCostEur) : formatCost(totalTokens);
  const lastDate = stellenanzeigen.length > 0
    ? formatDate(stellenanzeigen[0].created_at)
    : "—";

  return (
    <>
      <button className="btn-back" onClick={onBack}>Zurück</button>
      <PageHeader
        eyebrow="Partner"
        title={partner?.unternehmen || "Partner"}
        subtitle={partnerId ? `Partner-ID: ${partnerId}` : ""}
      />

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade …</span></div>
      ) : (
        <>
          <div className="partner-overview-grid">
            <div className="partner-overview-card">
              <div className="partner-overview-eyebrow">Stellenanzeigen</div>
              <div className="partner-overview-stats">
                <div>
                  <div className="partner-overview-num">{jfCount ?? "—"}</div>
                  <div className="partner-overview-label">eingereicht</div>
                </div>
                <div>
                  <div className="partner-overview-num">{generated}</div>
                  <div className="partner-overview-label">generiert</div>
                </div>
              </div>
              <button className="btn-secondary partner-overview-btn" onClick={onOpenStellenanzeigen}>
                Zu den Stellenanzeigen →
              </button>
            </div>

            <div className="partner-overview-card partner-overview-card--soft">
              <div className="partner-overview-eyebrow">Karriereportal-Texte</div>
              <div className="partner-overview-stats">
                <div>
                  <div className="partner-overview-num">0</div>
                  <div className="partner-overview-label">eingereicht</div>
                </div>
                <div>
                  <div className="partner-overview-num">0</div>
                  <div className="partner-overview-label">generiert</div>
                </div>
              </div>
              <span className="partner-overview-coming">Bald verfügbar</span>
            </div>

            <div className="partner-overview-card partner-overview-card--accent">
              <div className="partner-overview-eyebrow">Generierungs-Kosten</div>
              <div className="partner-overview-num partner-overview-num--big">{cost}</div>
              <div className="partner-overview-label">über alle bisherigen Generierungen</div>
            </div>
          </div>

          <h3 className="partner-overview-section-h">Letzte Stellenanzeigen</h3>
          {stellenanzeigen.length === 0 ? (
            <div className="empty-card card">
              <p>Noch keine Stellenanzeigen für diesen Partner.</p>
            </div>
          ) : (
            <div className="partner-overview-anzeigen">
              {stellenanzeigen.slice(0, 8).map((a) => (
                <div key={a.id} className="partner-overview-anzeige" onClick={onOpenStellenanzeigen}>
                  <div>
                    <div className="partner-overview-anzeige-title">{a.stellentitel}</div>
                    <div className="partner-overview-anzeige-meta">
                      {a.farbtyp} · {formatDate(a.created_at)}
                    </div>
                  </div>
                  <span className={"status-pill status-pill--" + (a.status || "entwurf") + " status-pill--readonly"}>
                    {(a.status || "entwurf").replace(/_/g, " ")}
                  </span>
                </div>
              ))}
              {stellenanzeigen.length > 8 && (
                <button className="btn-secondary" onClick={onOpenStellenanzeigen}>
                  Alle {stellenanzeigen.length} ansehen →
                </button>
              )}
            </div>
          )}

          <div className="partner-overview-meta">
            <span>Letzte Aktivität: <strong>{lastDate}</strong></span>
          </div>
        </>
      )}
    </>
  );
}

export default function PartnersPage({ onOpenPartner, isAdmin }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date-desc");
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const segmente = useSegmente();
  const detailId = segmente[0] === "partners" ? segmente[1] || null : null;
  const [duplikate, setDuplikate] = useState([]);
  const [merging, setMerging] = useState(null);
  const [dupeOffen, setDupeOffen] = useState(false);

  const loadDuplikate = useCallback(() => {
    fetch("/api/partners/duplikate", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setDuplikate(Array.isArray(d) ? d : []))
      .catch(() => setDuplikate([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/partners", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setPartners(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); loadDuplikate(); }, [load, loadDuplikate]);

  const openDetail = useCallback((p) => {
    navigate(`/partners/${p.partner_id}`);
  }, []);

  const closeDetail = useCallback(() => {
    navigate("/partners");
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partners
      .filter((p) => (showArchived ? true : !(p.archived === true || p.archived === "true")))
      .filter((p) => !q || (p.unternehmen || "").toLowerCase().includes(q));
  }, [partners, search, showArchived]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      switch (sort) {
        case "date-asc":
          return new Date(a.last_generated || 0) - new Date(b.last_generated || 0);
        case "name-asc":
          return (a.unternehmen || "").localeCompare(b.unternehmen || "", "de");
        case "name-desc":
          return (b.unternehmen || "").localeCompare(a.unternehmen || "", "de");
        case "date-desc":
        default:
          return new Date(b.last_generated || 0) - new Date(a.last_generated || 0);
      }
    });
    return arr;
  }, [filtered, sort]);

  const archivedCount = useMemo(
    () => partners.filter((p) => p.archived === true || p.archived === "true").length,
    [partners]
  );

  async function toggleArchive(p) {
    const isArchived = p.archived === true || p.archived === "true";
    await fetch(`/api/partners/${p.partner_id}/archive`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ archived: !isArchived }),
    });
    load();
  }

  async function deletePartner(p) {
    const count = parseInt(p.generated_count, 10) || 0;
    if (count > 0) {
      alert(`Dieser Partner hat ${count} Stellenanzeige${count !== 1 ? "n" : ""} und kann nicht gelöscht werden. Bitte zuerst die Stellenanzeigen entfernen.`);
      return;
    }
    if (!window.confirm(`Partner „${p.unternehmen}" in den Papierkorb verschieben? Du kannst ihn dort 30 Tage lang wiederherstellen.`)) return;
    const res = await fetch(`/api/partners/${p.partner_id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || "Löschen fehlgeschlagen");
      return;
    }
    load();
  }

  // Detail-View aktiv?
  if (detailId) {
    const partner = partners.find((p) => String(p.partner_id) === String(detailId));
    const goToJobs = () => {
      navigate(`/jobs/${detailId}`);
    };
    return (
      <PartnerDetail
        partner={partner}
        partnerId={detailId}
        onBack={closeDetail}
        onOpenStellenanzeigen={goToJobs}
      />
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Verwaltung"
        title="Partner"
        subtitle="Alle Partner-Unternehmen und ihre laufenden Projekte. Hier kannst du neue Partner anlegen, umbenennen oder abschließen."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} />
            Neuer Partner
          </button>
        }
      />

      {!loading && duplikate.length > 0 && (
        <div className="dupe-hinweis">
          <button className="dupe-hinweis-head" onClick={() => setDupeOffen(!dupeOffen)}>
            <Users size={15} />
            <span>
              {duplikate.length === 1
                ? "Ein Partner-Paar könnte dieselbe Firma sein"
                : `${duplikate.length} Partner-Paare könnten jeweils dieselbe Firma sein`}
            </span>
            <span className="dupe-hinweis-toggle">{dupeOffen ? "Ausblenden" : "Ansehen"}</span>
          </button>
          {dupeOffen && (
            <div className="dupe-liste">
              <p className="dupe-erklaerung">
                Nur ein Vorschlag – es wird nichts automatisch geändert. Prüfe selbst, ob es wirklich
                dieselbe Firma ist.
              </p>
              {duplikate.map((d, i) => (
                <div className="dupe-paar" key={i}>
                  <div className="dupe-firmen">
                    <strong>{d.a.unternehmen}</strong>
                    <span className="dupe-anzahl">{d.a.anzeigen} Anzeigen</span>
                    <span className="dupe-vs">und</span>
                    <strong>{d.b.unternehmen}</strong>
                    <span className="dupe-anzahl">{d.b.anzeigen} Anzeigen</span>
                    {d.identisch && <span className="dupe-badge">gleicher Name</span>}
                  </div>
                  {isAdmin ? (
                    <button className="btn-secondary-sm" onClick={() => setMerging(d)}>
                      Zusammenführen …
                    </button>
                  ) : (
                    <span className="dupe-hint">Nur Admins können zusammenführen</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && partners.length > 0 && (
        <div className="toolbar">
          <div className="search-input-wrap toolbar-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Partner suchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch("")}>&times;</button>
            )}
          </div>
          <div className="toolbar-select">
            <ArrowUpDown size={14} />
            <select
              className="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sortierung"
            >
              <option value="date-desc">Neueste zuerst</option>
              <option value="date-asc">Älteste zuerst</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
            </select>
          </div>
          {archivedCount > 0 && (
            <button
              className={`btn-secondary ${showArchived ? "active" : ""}`}
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive size={14} />
              Abgeschlossene ({archivedCount})
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Partner …</span></div>
      ) : sorted.length === 0 ? (
        <div className="empty-card card">
          <Users size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>
            {search
              ? "Keine Partner gefunden."
              : showArchived
                ? "Keine abgeschlossenen Partner."
                : "Noch keine Partner angelegt. Erstelle den ersten über „Neuer Partner“."}
          </p>
        </div>
      ) : (
        <div className="partner-table-wrap">
          <table className="partner-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th className="num">Stellentitel</th>
                <th className="num">Generiert</th>
                <th className="num">Karriereportal</th>
                <th className="num">Kosten</th>
                <th>Letzte Anzeige</th>
                <th className="actions"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => {
                const isArchived = p.archived === true || p.archived === "true";
                const genCount = parseInt(p.generated_count, 10) || 0;
                const stellenCount = parseInt(p.count, 10) || 0;
                return (
                  <tr
                    key={p.partner_id}
                    className={"partner-tr" + (isArchived ? " partner-tr--archived" : "")}
                    onClick={() => openDetail(p)}
                  >
                    <td className="partner-cell-name">
                      <span className="partner-row-avatar">{(p.unternehmen || "?")[0].toUpperCase()}</span>
                      <span className="partner-cell-text">
                        <span className="partner-cell-title">{p.unternehmen}</span>
                        {isArchived && <span className="partner-row-tag">Abgeschlossen</span>}
                      </span>
                    </td>
                    <td className="num">{stellenCount}</td>
                    <td className="num">{genCount}</td>
                    <td className="num partner-cell-muted">—</td>
                    <td className="num">{p.cost_eur != null ? formatEur(p.cost_eur) : formatCost(p.total_tokens)}</td>
                    <td className="partner-cell-muted">{formatDate(p.last_generated)}</td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-icon"
                        onClick={() => setRenaming(p)}
                        data-tooltip="Umbenennen"
                        aria-label="Umbenennen"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => toggleArchive(p)}
                        data-tooltip={isArchived ? "Wiederherstellen" : "Abschließen"}
                        aria-label={isArchived ? "Wiederherstellen" : "Abschließen"}
                      >
                        {isArchived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => deletePartner(p)}
                        data-tooltip="Löschen"
                        aria-label="Löschen"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreatePartnerModal
          existingNames={partners.map((p) => (p.unternehmen || "").toLowerCase())}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {renaming && (
        <RenamePartnerModal
          partner={renaming}
          existingNames={partners
            .filter((p) => p.partner_id !== renaming.partner_id)
            .map((p) => (p.unternehmen || "").toLowerCase())}
          onClose={() => setRenaming(null)}
          onRenamed={() => { setRenaming(null); load(); }}
        />
      )}

      {merging && (
        <MergePartnersModal
          paar={merging}
          onClose={() => setMerging(null)}
          onMerged={() => { setMerging(null); load(); loadDuplikate(); }}
        />
      )}
    </>
  );
}

function MergePartnersModal({ paar, onClose, onMerged }) {
  const [keepId, setKeepId] = useState(paar.a.anzeigen >= paar.b.anzeigen ? paar.a.partner_id : paar.b.partner_id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const behalten = paar.a.partner_id === keepId ? paar.a : paar.b;
  const entfernen = paar.a.partner_id === keepId ? paar.b : paar.a;

  const merge = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/partners/merge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ keepId: behalten.partner_id, removeId: entfernen.partner_id }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Zusammenführen fehlgeschlagen");
      onMerged();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3>Partner zusammenführen</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="dupe-erklaerung">Welcher Partner soll bestehen bleiben?</p>
          {[paar.a, paar.b].map((p) => (
            <label className="dupe-wahl" key={p.partner_id}>
              <input
                type="radio"
                name="keep"
                checked={keepId === p.partner_id}
                onChange={() => setKeepId(p.partner_id)}
              />
              <span>
                <strong>{p.unternehmen}</strong>
                <span className="dupe-anzahl">{p.anzeigen} Anzeigen</span>
              </span>
            </label>
          ))}

          <div className="dupe-warnung">
            <strong>{entfernen.unternehmen}</strong> wird gelöscht. Die {entfernen.anzeigen} Anzeigen,
            Projekte und Jobs werden auf <strong>{behalten.unternehmen}</strong> übertragen. Das lässt
            sich nicht rückgängig machen.
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn-secondary" onClick={onClose} disabled={busy}>Abbrechen</button>
            <button className="btn-primary" onClick={merge} disabled={busy}>
              {busy ? "Führe zusammen …" : "Zusammenführen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreatePartnerModal({ existingNames, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (existingNames.includes(trimmed.toLowerCase())) {
      setError("Ein Partner mit diesem Namen existiert bereits.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/partners", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ unternehmen: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erstellen fehlgeschlagen");
      onCreated(data);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h3>Neuer Partner</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="new-partner-name">Unternehmensname</label>
              <input
                id="new-partner-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(""); }}
                placeholder="z.B. Müller Bau GmbH"
                autoFocus
                required
              />
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
                {submitting ? "Wird angelegt …" : "Partner anlegen"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function RenamePartnerModal({ partner, existingNames, onClose, onRenamed }) {
  const [name, setName] = useState(partner.unternehmen || "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === partner.unternehmen) { onClose(); return; }
    if (existingNames.includes(trimmed.toLowerCase())) {
      setError("Ein Partner mit diesem Namen existiert bereits.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`/api/partners/${partner.partner_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ unternehmen: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Umbenennen fehlgeschlagen");
      onRenamed();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <h3>Partner umbenennen</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="rename-partner-name">Neuer Unternehmensname</label>
              <input
                id="rename-partner-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setError(""); }}
                autoFocus
                required
              />
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={submitting || !name.trim()}>
                {submitting ? "Wird gespeichert …" : "Speichern"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
